import { Button } from "@/components/ui/UIElements/Button";
import React, { useEffect, useState } from "react";
import { NativeSelect } from "@/components/ui/UIElements/Select";
import { Switch, Input, Icon, Modal } from "../../";

import TimeEditor from "./TimeEditor";
import BusinessZoneModal from "./BusinessZoneModal";
import {
  numOnly,
  priorityList,
  defaultScheduleStr,
} from "./BusinessZones.utils";

const BusinessZonesStep = ({
  businessId,
  zones = [],
  deliveryTime,
  pickupTime,
  cancelOrderAfterMinutes,
  orderDefaultPriority,
  minimum,
  businessAddress,
  businessLocation,
  onUpdate,
  onToggleZone,
  onCreateZone,
  onUpdateZone,
  onDeleteZone,
  onUpdateZoneBusinesses,
}) => {
  const [tab, setTab] = useState("delivery");

  const [cancelMinutes, setCancelMinutes] = useState(
    String(cancelOrderAfterMinutes ?? ""),
  );
  const [priority, setPriority] = useState(
    orderDefaultPriority != null ? String(orderDefaultPriority) : "0",
  );
  const [pickupMinimum, setPickupMinimum] = useState(String(minimum ?? ""));

  // Individual saving states
  const [savingDeliveryTime, setSavingDeliveryTime] = useState(false);
  const [savingCancelMinutes, setSavingCancelMinutes] = useState(false);
  const [savingPriority, setSavingPriority] = useState(false);
  const [savingPickupTime, setSavingPickupTime] = useState(false);
  const [savingMinimum, setSavingMinimum] = useState(false);

  const [showZoneForm, setShowZoneForm] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [deletingZone, setDeletingZone] = useState(null);

  const [zoneForm, setZoneForm] = useState({
    name: "",
    type: 4,
    minimum: "",
    price: "",
    data: null,
  });

  const resetZoneForm = () => {
    setZoneForm({ name: "", type: 4, minimum: "", price: "", data: null });
    setEditingZone(null);
  };

  const handleOpenCreateZone = () => {
    resetZoneForm();
    setShowZoneForm(true);
  };

  const handleOpenEditZone = (zone) => {
    let parsedData = zone.data || null;
    if (parsedData && typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (error) {
        console.error("Error parsing zone data:", error);
        parsedData = null;
      }
    }

    setZoneForm({
      name: zone.name || "",
      type: zone.type || 4,
      minimum: String(zone.minimum ?? ""),
      price: String(zone.price ?? ""),
      data: parsedData,
    });
    setEditingZone(zone);
    setShowZoneForm(true);
  };

  const handleSubmitZone = async () => {
    const payloadBase = {
      name: zoneForm.name.trim(),
      type: Number(zoneForm.type),
      minimum: String(zoneForm.minimum),
      price: String(zoneForm.price),
      enabled: true,
    };

    const payload = { ...payloadBase };
    if (Number(zoneForm.type) === 4) {
      payload.data = "null";
    } else if (Number(zoneForm.type) === 1) {
      // Circle
      payload.data = JSON.stringify(zoneForm.data || null);
    } else if (Number(zoneForm.type) === 2) {
      // Polygon
      payload.data = JSON.stringify(zoneForm.data || null);
    }

    if (!editingZone) {
      payload.schedule = defaultScheduleStr;
      await onCreateZone?.(payload);
    } else {
      // Build diff update
      const diff = {};
      if (editingZone.name !== payloadBase.name) diff.name = payloadBase.name;
      if (editingZone.type !== payloadBase.type) diff.type = payloadBase.type;
      if (String(editingZone.minimum) !== payloadBase.minimum)
        diff.minimum = payloadBase.minimum;
      if (String(editingZone.price) !== payloadBase.price)
        diff.price = payloadBase.price;
      if (Number(zoneForm.type) === 4) {
        if (editingZone.data !== null) diff.data = null;
      } else if (Number(zoneForm.type) === 1 || Number(zoneForm.type) === 2) {
        // Circle or Polygon
        const dataStr = JSON.stringify(zoneForm.data || null);
        if (
          JSON.stringify(editingZone.data || null) !==
          JSON.stringify(zoneForm.data || null)
        )
          diff.data = dataStr;
      }
      if (Object.keys(diff).length) {
        await onUpdateZone?.(editingZone.id, diff);
      }
    }
    setShowZoneForm(false);
    resetZoneForm();
  };

  useEffect(() => {
    setCancelMinutes(String(cancelOrderAfterMinutes ?? ""));
  }, [cancelOrderAfterMinutes]);

  useEffect(() => {
    setPriority(
      orderDefaultPriority != null ? String(orderDefaultPriority) : "0",
    );
  }, [orderDefaultPriority]);

  useEffect(() => {
    setPickupMinimum(String(minimum ?? ""));
  }, [minimum]);

  return (
    <div className="space-y-6">
      <div className="border-border border-b">
        <nav className="flex space-x-6 overflow-x-auto">
          {[
            { id: "delivery", label: "Delivery" },
            { id: "pickup", label: "Pickup" },
          ].map((t) => (
            <Button
              variant="ghost"
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`border-b-2 px-1 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                tab === t.id
                  ? "border-primary text-white"
                  : "text-muted-foreground hover:border-border hover:text-foreground border-transparent"
              }`}
            >
              {t.label}
            </Button>
          ))}
        </nav>
      </div>

      {tab === "delivery" && (
        <div className="space-y-6">
          {/* Order time first */}
          <div className="border-border bg-card/60 rounded-xl border p-4">
            <TimeEditor
              label="Delivery order time (HH:MM)"
              value={deliveryTime}
              onSave={async (val) => {
                try {
                  setSavingDeliveryTime(true);
                  await onUpdate({ delivery_time: val });
                } finally {
                  setSavingDeliveryTime(false);
                }
              }}
              isSaving={savingDeliveryTime}
            />
          </div>

          {/* Zones list */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-base font-medium text-white">
                Delivery Zones
              </h3>
              <Button
                variant="default"
                onClick={handleOpenCreateZone}
                className="flex items-center gap-1 px-3 py-2"
              >
                <Icon name="Plus" className="h-4 w-4" /> Add Zone
              </Button>
            </div>
            <div className="border-border bg-card/60 overflow-hidden rounded-xl border">
              <div className="border-border bg-secondary text-foreground grid grid-cols-12 border-b px-4 py-3 text-xs sm:text-sm">
                <div className="col-span-6">Name</div>
                <div className="col-span-2">Minimum</div>
                <div className="col-span-2">Price</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {!zones || zones.length === 0 ? (
                <div className="text-foreground p-8 text-center">
                  No zones found
                </div>
              ) : (
                zones.map((z) => (
                  <div
                    key={z.id}
                    className="border-border hover:bg-secondary/40 grid grid-cols-12 items-center border-t px-4 py-3 text-white"
                  >
                    <div className="col-span-6">
                      <div className="mr-6 flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium" title={z.name}>
                          {z.name}
                        </span>
                        {businessId === z.business_id &&
                          z?.businesses?.length > 1 && (
                            <span
                              className="border-primary/30 bg-primary/20 inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium text-[var(--od-action-content)]"
                              title={`Shared with ${z.businesses.length - 1} other business${z.businesses.length - 1 > 1 ? "es" : ""}`}
                            >
                              <Icon name="Users" className="h-3 w-3" />
                              <span>Shared</span>
                            </span>
                          )}
                      </div>
                    </div>
                    <div className="col-span-2">{z.minimum ?? "—"}</div>
                    <div className="col-span-2">{z.price ?? "—"}</div>
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <Switch
                        checked={!!z.enabled}
                        onCheckedChange={(value) => onToggleZone(z.id, value)}
                      />
                      <Button
                        variant="ghost"
                        onClick={() => handleOpenEditZone(z)}
                        className="border-border bg-card hover:bg-secondary rounded border px-2 py-1 text-xs"
                        title="Edit zone"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setDeletingZone(z)}
                        className={`border-destructive/60 text-destructive hover:bg-destructive/30 rounded border px-2 py-1 text-xs ${businessId === z.business_id && z?.businesses?.length > 1 ? "cursor-not-allowed opacity-50" : ""}`}
                        title="Delete zone"
                        disabled={
                          businessId === z.business_id &&
                          z?.businesses?.length > 1
                        }
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Bottom side-by-side controls */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="border-border bg-card/60 rounded-xl border p-4">
              <label
                htmlFor="business-order-default-priority"
                className="text-foreground mb-2 block text-sm font-medium"
              >
                Cancel order after minutes
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={cancelMinutes}
                  onChange={(e) => setCancelMinutes(numOnly(e.target.value))}
                  placeholder="e.g. 1441"
                />
                <Button
                  variant="default"
                  onClick={async () => {
                    try {
                      setSavingCancelMinutes(true);
                      await onUpdate({
                        cancel_order_after_minutes: cancelMinutes,
                      });
                    } finally {
                      setSavingCancelMinutes(false);
                    }
                  }}
                  disabled={
                    savingCancelMinutes ||
                    String(cancelOrderAfterMinutes ?? "") ===
                      String(cancelMinutes)
                  }
                  className="flex items-center justify-center px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingCancelMinutes ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </div>
            <div className="border-border bg-card/60 rounded-xl border p-4">
              <label className="text-foreground mb-2 block text-sm font-medium">
                Order default priority
              </label>
              <div className="flex items-center gap-2">
                <NativeSelect
                  id="business-order-default-priority"
                  className="h-10 w-full"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {priorityList.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.content}
                    </option>
                  ))}
                </NativeSelect>
                <Button
                  variant="default"
                  onClick={async () => {
                    try {
                      setSavingPriority(true);
                      await onUpdate({ order_default_priority: priority });
                    } finally {
                      setSavingPriority(false);
                    }
                  }}
                  disabled={
                    savingPriority ||
                    String(orderDefaultPriority ?? "0") === String(priority)
                  }
                  className="flex items-center justify-center px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingPriority ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "pickup" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="border-border bg-card/60 rounded-xl border p-4">
              <TimeEditor
                label="Pickup order time (HH:MM)"
                value={pickupTime}
                onSave={async (val) => {
                  try {
                    setSavingPickupTime(true);
                    await onUpdate({ pickup_time: val });
                  } finally {
                    setSavingPickupTime(false);
                  }
                }}
                isSaving={savingPickupTime}
              />
            </div>
            <div className="border-border bg-card/60 rounded-xl border p-4">
              <label className="text-foreground mb-2 block text-sm font-medium">
                Minimum purchase
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={pickupMinimum}
                  onChange={(e) => setPickupMinimum(numOnly(e.target.value))}
                  placeholder="e.g. 3"
                />
                <Button
                  variant="default"
                  onClick={async () => {
                    try {
                      setSavingMinimum(true);
                      await onUpdate({ minimum: pickupMinimum });
                    } finally {
                      setSavingMinimum(false);
                    }
                  }}
                  disabled={
                    savingMinimum ||
                    String(minimum ?? "") === String(pickupMinimum)
                  }
                  className="flex items-center justify-center px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingMinimum ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Create/Edit Zone Modal */}
      {showZoneForm ? (
        <BusinessZoneModal
          isOpen={true}
          onClose={() => {
            setShowZoneForm(false);
            resetZoneForm();
          }}
          editingZone={editingZone}
          zoneForm={zoneForm}
          setZoneForm={setZoneForm}
          businessAddress={businessAddress}
          businessLocation={businessLocation}
          businessId={businessId}
          onSubmitZone={handleSubmitZone}
          onUpdateZoneBusinesses={onUpdateZoneBusinesses}
        />
      ) : null}

      {/* Delete Zone Confirm */}
      {deletingZone && (
        <Modal
          className="ordering-catalog-surface"
          isOpen={true}
          onClose={() => setDeletingZone(null)}
          title="Delete Zone"
          subtitle="Are you sure you want to delete this zone?"
          maxWidth="max-w-md"
        >
          <div className="space-y-4">
            <div className="border-destructive bg-destructive/10 dark:border-destructive dark:bg-destructive/20 rounded-lg border p-4">
              <div className="flex items-center space-x-3">
                <div>
                  <h4 className="text-destructive dark:text-destructive text-sm font-medium">
                    {deletingZone.name}
                  </h4>
                  <p className="text-destructive dark:text-destructive mt-1 text-xs">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex space-x-3 pt-4">
              <Button
                variant="ghost"
                onClick={async () => {
                  await onDeleteZone?.(deletingZone.id);
                  setDeletingZone(null);
                }}
                className="bg-destructive hover:bg-destructive/90 flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
              >
                Delete Zone
              </Button>
              <Button
                variant="ghost"
                onClick={() => setDeletingZone(null)}
                className="text-card-foreground bg-card border-border hover:bg-accent flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BusinessZonesStep;
